import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Loader from 'components/Loader'
import ScheduledBackgroundJobDrawer from 'components/Drawers/ScheduledBackgroundJobDrawer'
import {
  deleteScheduledBackgroundJob,
  fetchScheduledBackgroundJobs,
  getBackgroundJobTypeLabel,
  runScheduledBackgroundJob,
  SCHEDULED_BACKGROUND_JOB_KIND,
  SCHEDULED_BACKGROUND_JOB_KINDS,
} from 'src/redux/scheduledBackgroundJobs'

const ScheduledBackgroundJobs = () => {
  const dispatch = useDispatch()
  const { list = [], loading } = useSelector(state => state.scheduledBackgroundJobs)
  const [selectedKind, setSelectedKind] = useState(SCHEDULED_BACKGROUND_JOB_KINDS[0].value)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedScheduledBackgroundJob, setSelectedScheduledBackgroundJob] = useState(null)

  useEffect(() => {
    dispatch(fetchScheduledBackgroundJobs())
  }, [dispatch])

  // System jobs are defined in code, so they cannot be created or edited from here.
  const canManageSelectedKind = selectedKind !== SCHEDULED_BACKGROUND_JOB_KIND.SYSTEM
  const visibleScheduledBackgroundJobs = list.filter(job => job.kind === selectedKind)

  const handleOpenNew = () => {
    setSelectedScheduledBackgroundJob(null)
    setDrawerOpen(true)
  }

  const handleOpenEdit = (scheduledBackgroundJob) => {
    setSelectedScheduledBackgroundJob(scheduledBackgroundJob)
    setDrawerOpen(true)
  }

  const handleClose = () => {
    setDrawerOpen(false)
    setSelectedScheduledBackgroundJob(null)
  }

  const handleRun = (scheduledBackgroundJob) => {
    dispatch(runScheduledBackgroundJob(scheduledBackgroundJob.kind, scheduledBackgroundJob.name))
  }

  const handleDelete = (scheduledBackgroundJob) => {
    dispatch(deleteScheduledBackgroundJob(scheduledBackgroundJob.kind, scheduledBackgroundJob.name))
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant='h5'>Scheduled Background Jobs</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size='small' sx={{ minWidth: 160 }}>
            <InputLabel id='kind-filter-label'>Kind</InputLabel>
            <Select
              labelId='kind-filter-label'
              label='Kind'
              value={selectedKind}
              onChange={(event) => setSelectedKind(event.target.value)}
            >
              {SCHEDULED_BACKGROUND_JOB_KINDS.map(kind => (
                <MenuItem key={kind.value} value={kind.value}>
                  {kind.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {canManageSelectedKind && (
            <Button variant='contained' onClick={handleOpenNew}>
              New
            </Button>
          )}
        </Box>
      </Box>

      {loading ? (
        <Loader />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Cron Schedule</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleScheduledBackgroundJobs.map(scheduledBackgroundJob => {
                const canManage = scheduledBackgroundJob.kind !== SCHEDULED_BACKGROUND_JOB_KIND.SYSTEM
                return (
                  <TableRow key={`${scheduledBackgroundJob.kind}-${scheduledBackgroundJob.name}`}>
                    <TableCell>{scheduledBackgroundJob.name}</TableCell>
                    <TableCell>
                      {getBackgroundJobTypeLabel(scheduledBackgroundJob.backgroundJobType)}
                    </TableCell>
                    <TableCell>{scheduledBackgroundJob.cronSchedule || ''}</TableCell>
                    <TableCell>
                      <Button size='small' onClick={() => handleRun(scheduledBackgroundJob)}>
                        Run
                      </Button>
                      {canManage && (
                        <Button size='small' onClick={() => handleOpenEdit(scheduledBackgroundJob)}>
                          Edit
                        </Button>
                      )}
                      {canManage && (
                        <Button size='small' color='error' onClick={() => handleDelete(scheduledBackgroundJob)}>
                          Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              {visibleScheduledBackgroundJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align='center'>
                    No scheduled background jobs.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ScheduledBackgroundJobDrawer
        open={drawerOpen}
        onClose={handleClose}
        kind={selectedKind}
        scheduledBackgroundJob={selectedScheduledBackgroundJob}
      />
    </Box>
  )
}

export default ScheduledBackgroundJobs
